import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  NavigationAction,
  NavigationCardLink,
  NavigationDropdown,
  NavigationItem,
  NavigationLink
} from '../../../../vex-core/navigation/navigation-item.interface';
import { activeCardRow } from '../../../../vex-core/navigation/navigation-menu.builder';
import { dropdownAnimation } from '@vex/animations/dropdown.animation';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive
} from '@angular/router';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { NavigationService } from '../../../../vex-core/navigation/navigation.service';
import { VexLayoutService } from '@vex/services/vex-layout.service';

import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** How long the pointer may be off both the item and its card before the card closes. */
const CARD_CLOSE_DELAY_MS = 180;
/** A short intent delay so sweeping the pointer down the menu does not flash every card. */
const CARD_OPEN_DELAY_MS = 90;

@Component({
  selector: 'vex-sidenav-item',
  templateUrl: './sidenav-item.component.html',
  styleUrls: ['./sidenav-item.component.scss'],
  animations: [dropdownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    MatRippleModule,
    RouterLinkActive,
    RouterLink,
    MatIconModule,
    MatTooltipModule,
    OverlayModule,
    NgClass,
    NgFor
  ]
})
export class SidenavItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) item!: NavigationItem;
  @Input({ required: true }) level!: number;
  isOpen: boolean = false;
  isActive: boolean = false;

  isLink = this.navigationService.isLink;
  isDropdown = this.navigationService.isDropdown;
  isSubheading = this.navigationService.isSubheading;
  isAction = this.navigationService.isAction;

  /** Whether the sub-page card beside this item is showing. */
  cardOpen = false;
  /** Live numbers per card row key, from the card's `refresh`. */
  cardBadges: Record<string, NavigationLink['badge']> = {};
  /** Card rows the last refresh asked to leave out. */
  cardHidden = new Set<string>();

  /** Whether the sidenav is folded to its icon rail (labels hidden, so the label shows as a tooltip). */
  collapsed = false;
  /** The card row for the page being shown, marked inside the card. */
  activeRow: NavigationCardLink | null = null;

  /** Card to the right of the item, top-aligned; flips to bottom-aligned near the screen's end. */
  readonly cardPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 8, offsetY: -6 },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 8, offsetY: 6 }
  ];

  @ViewChild('cardPanel') private cardPanel?: ElementRef<HTMLElement>;
  @ViewChild('linkEl') private linkEl?: ElementRef<HTMLElement>;

  /** Set while focus is handed back to the item after Escape, so that focus does not reopen it. */
  private refocusing = false;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly layoutService = inject(VexLayoutService);

  constructor(
    private router: Router,
    private cd: ChangeDetectorRef,
    private navigationService: NavigationService
  ) { }

  @HostBinding('class')
  get levelClass() {
    return `item-level-${this.level}`;
  }

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        // Any navigation (e.g. a click inside the card) closes the card.
        if (this.cardOpen) this.closeCard();
        if (this.isDropdown(this.item)) this.onRouteChange();
        this.updateActiveRow();
      });

    // Folding or unfolding moves the item's edge; a card placed against the old edge is closed
    // rather than left floating over the menu.
    this.layoutService.sidenavCollapsed$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(collapsed => {
        this.collapsed = collapsed;
        this.closeCard();
        this.cd.markForCheck();
      });

    this.navigationService.openChange$
      .pipe(
        filter(() => this.isDropdown(this.item)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((item) => this.onOpenChange(item));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      changes.hasOwnProperty('item') &&
      this.isDropdown(this.item)
    ) {
      this.onRouteChange();
    }
    if (changes?.['item']) this.updateActiveRow();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
    this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
    this.cd.markForCheck();
  }

  onOpenChange(item: NavigationDropdown) {
    if (this.isChildrenOf(this.item as NavigationDropdown, item)) {
      return;
    }

    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      return;
    }

    if (this.item !== item) {
      this.isOpen = false;
      this.cd.markForCheck();
    }
  }

  onRouteChange() {
    if (this.hasActiveChilds(this.item as NavigationDropdown)) {
      this.isActive = true;
      this.isOpen = true;
      this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
      this.cd.markForCheck();
    } else {
      this.isActive = false;
      this.isOpen = false;
      this.navigationService.triggerOpenChange(this.item as NavigationDropdown);
      this.cd.markForCheck();
    }
  }

  isChildrenOf(parent: NavigationDropdown, item: NavigationDropdown): boolean {
    if (parent.children.indexOf(item) !== -1) {
      return true;
    }

    return parent.children
      .filter((child) => this.isDropdown(child))
      .some((child) => this.isChildrenOf(child as NavigationDropdown, item));
  }

  hasActiveChilds(parent: NavigationDropdown): boolean {
    return parent.children.some((child) => {
      if (this.isDropdown(child)) {
        return this.hasActiveChilds(child);
      }

      if (this.isLink(child) && !this.isFunction(child.route)) {
        return this.router.isActive(child.route as string, false);
      }
    });
  }

  isFunction(prop: NavigationLink['route']): boolean {
    return prop instanceof Function;
  }

  visibleContainer(item: NavigationDropdown): boolean {
    let isVisible = true;
    // find if all children are one of the two: invisible or unavailable
    let disqualified = 0;
    item.children.forEach((child) => {
      if (child.disabled) {
        disqualified++;
      }
    });
    if (disqualified === item.children.length) {
      isVisible = false;
    }
    return isVisible;
  }

  /** Keeps child rows (and their open cards) alive across menu rebuilds. */
  trackChild(index: number, child: NavigationItem): string {
    if (child.type === 'link' && typeof child.route === 'string') {
      return child.route + JSON.stringify(child.queryParams ?? {}) + child.label;
    }
    return child.type + ':' + child.label;
  }

  // ==================== Action ("+ New project") ====================

  run(action: NavigationAction): void {
    action.run();
  }

  // ==================== Sub-page card ====================

  get link(): NavigationLink | null {
    return this.isLink(this.item) ? this.item : null;
  }

  /** Card rows in their groups, without the rows the last refresh hid. */
  visibleRows(links: NavigationCardLink[]): NavigationCardLink[] {
    return links.filter(l => !l.key || !this.cardHidden.has(l.key));
  }

  rowBadge(row: NavigationCardLink): NavigationLink['badge'] {
    return (row.key && this.cardBadges[row.key]) || row.badge;
  }

  /** Pointer entered the item: open after a short intent delay. */
  onItemEnter(): void {
    if (!this.link?.card) return;
    this.cancelClose();
    if (this.cardOpen || this.openTimer) return;
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.openCard();
    }, CARD_OPEN_DELAY_MS);
  }

  /** Pointer left the item or the card: close unless it comes back in time. */
  onLeave(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    if (!this.cardOpen) return;
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.closeCard(), CARD_CLOSE_DELAY_MS);
  }

  /** Pointer is on the card: keep it open while it is used. */
  onCardEnter(): void {
    this.cancelClose();
  }

  /** Pointer left the card: close unless it comes back in time. */
  onCardLeave(): void {
    this.onLeave();
  }

  /** A row was picked: close the card. */
  onCardRowClick(): void {
    this.closeCard();
  }

  /** Keyboard focus on the item opens its card straight away. */
  onItemFocus(): void {
    if (!this.link?.card || this.refocusing) return;
    this.cancelClose();
    this.openCard();
  }

  /** Focus left the item: keep the card only if focus went into it. */
  onItemBlur(event: FocusEvent): void {
    if (this.focusInsideCard(event.relatedTarget)) return;
    this.onLeave();
  }

  /** Focus left a card row: close unless it moved to another row or back to the item. */
  onCardFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (this.focusInsideCard(next) || (next && this.linkEl?.nativeElement.contains(next))) return;
    this.onLeave();
  }

  /** → from the item moves into the card. */
  focusCard(event: Event): void {
    if (!this.cardOpen) return;
    event.preventDefault();
    this.cardPanel?.nativeElement.querySelector<HTMLElement>('a, [tabindex]')?.focus();
  }

  /** Escape (or ←) inside the card closes it and hands focus back to the item. */
  closeCardToItem(event?: Event): void {
    event?.preventDefault();
    this.closeCard();
    this.refocusing = true;
    this.linkEl?.nativeElement.focus();
    this.refocusing = false;
  }

  closeCard(): void {
    this.clearTimers();
    if (!this.cardOpen) return;
    this.cardOpen = false;
    this.cd.markForCheck();
  }

  private openCard(): void {
    const card = this.link?.card;
    if (!card || this.cardOpen) return;
    this.cardOpen = true;
    this.cd.markForCheck();
    // Live numbers are asked only now that somebody is looking; a failure keeps the rows bare.
    card.refresh?.().then(update => {
      this.cardBadges = update.badges ?? {};
      this.cardHidden = new Set(update.hidden ?? []);
      this.cd.markForCheck();
    }).catch(() => { /* no numbers is fine — the rows still link to their pages */ });
  }

  /** Finds the card row matching the current address (path + query params). */
  private updateActiveRow(): void {
    const card = this.link?.card;
    let row: NavigationCardLink | null = null;
    if (card) {
      const tree = this.router.parseUrl(this.router.url);
      const path = '/' + (tree.root.children['primary']?.segments.map(s => s.path).join('/') ?? '');
      // Repeated query params arrive as arrays; a card row only ever names single values.
      const query: Record<string, string> = {};
      for (const [k, v] of Object.entries(tree.queryParams)) query[k] = Array.isArray(v) ? String(v[0]) : String(v);
      row = activeCardRow(card, path, query);
    }
    if (row === this.activeRow) return;
    this.activeRow = row;
    this.cd.markForCheck();
  }

  private focusInsideCard(target: EventTarget | null): boolean {
    return !!target && !!this.cardPanel?.nativeElement.contains(target as Node);
  }

  private cancelClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private clearTimers(): void {
    this.cancelClose();
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }
}
